import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { authApi } from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';
import { validatePasswordPolicy } from '../../utils/accountCreatePayload';

/**
 * Принудительная смена пароля (must_change_password).
 * PATCH своего аккаунта, затем обновление /me и переход в приложение.
 */
export default function ChangePassword() {
  const navigate = useNavigate();
  const { user, logout, refreshUserFromMe } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const profileUid = user?.profile_uid;
  const accountUid = user?.account_id || user?.account_uid;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!currentPassword) {
      setError('Введите текущий пароль');
      return;
    }
    const passwordError = validatePasswordPolicy(password, confirmPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (!profileUid || !accountUid) {
      setError('Не удалось определить аккаунт. Выйдите и войдите снова.');
      return;
    }

    setLoading(true);
    try {
      await authApi.patch(`/profiles/${profileUid}/accounts/${accountUid}/password`, {
        current_password: currentPassword,
        new_password: password,
      });
      const updated = await refreshUserFromMe();
      if (updated?.must_change_password) {
        setError('Пароль сохранён, но флаг смены ещё активен. Обновите страницу или войдите снова.');
        return;
      }
      navigate('/', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Не удалось сменить пароль';
      setError(Array.isArray(detail) ? detail.join(', ') : detail);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 420 }}>
        <Typography variant="h5" gutterBottom>
          Смена пароля
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Для продолжения работы задайте новый пароль. Старый больше использовать нельзя.
        </Typography>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            type="password"
            label="Текущий пароль"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={loading}
            autoComplete="current-password"
            margin="normal"
          />
          <TextField
            fullWidth
            type="password"
            label="Новый пароль"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            autoComplete="new-password"
            margin="normal"
            helperText="Не менее 8 символов, строчная и заглавная буква, цифра"
          />
          <TextField
            fullWidth
            type="password"
            label="Повторите новый пароль"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            autoComplete="new-password"
            margin="normal"
          />
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" fullWidth disabled={loading}>
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Сохранить пароль'}
            </Button>
            <Button variant="text" fullWidth disabled={loading} onClick={handleLogout}>
              Выйти
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
}
